import { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { ShieldOff, Trash2, UserPlus, Users, ChevronRight } from 'lucide-react';
import { Card } from '@/components/ui/Card';
import { Spinner } from '@/components/ui/Spinner';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { Badge } from '@/components/ui/Badge';
import { EmptyState } from '@/components/ui/EmptyState';
import { Pagination } from '@/components/ui/Pagination';
import { Select } from '@/components/ui/Select';
import { BulkInviteModal } from '@/components/admin/BulkInviteModal';
import { useDeleteUser, useDisableUser, useInviteUser, useInvites, useResendInvite, useRevokeInvite, useResetUserMfa, useUpdateUser, useUsers } from '@/api/users';
import { useOrganizations } from '@/api/organizations';
import { useAuth } from '@/hooks/useAuth';
import { usePageTitle } from '@/hooks/usePageTitle';
import { useToast } from '@/hooks/useToast';
import { apiError } from '@/lib/api';
import type { Role, User } from '@/types/api';

const PAGE_LIMIT = 50;

const STAFF_ROLE_OPTIONS = [
  { value: 'sysadmin', label: 'System admin' },
  { value: 'manager', label: 'Manager' },
  { value: 'staff', label: 'Staff' },
];

const ALL_ROLE_OPTIONS = [
  ...STAFF_ROLE_OPTIONS,
  { value: 'client_admin', label: 'Client admin' },
  { value: 'client_user', label: 'Client user' },
];

function roleLabel(role: string) {
  return ALL_ROLE_OPTIONS.find((o) => o.value === role)?.label ?? role;
}

export function UsersPage({ staffOnly = false }: { staffOnly?: boolean }) {
  usePageTitle('Users');
  const { user: me } = useAuth();
  const [offset, setOffset] = useState(0);
  const { data, isLoading, isError, error } = useUsers({ limit: PAGE_LIMIT, offset, staff_only: staffOnly });
  const orgs = useOrganizations();
  const invite = useInviteUser();
  const disable = useDisableUser();
  const deleteUser = useDeleteUser();
  const updateUser = useUpdateUser();
  const resetMfa = useResetUserMfa();
  const invites = useInvites();
  const resendInvite = useResendInvite();
  const revokeInvite = useRevokeInvite();
  const toast = useToast();

  const [open, setOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [fullName, setFullName] = useState('');
  const [role, setRole] = useState<Role>('staff');
  const [orgId, setOrgId] = useState<string>('');
  const [deleteTarget, setDeleteTarget] = useState<User | null>(null);
  const [disableTarget, setDisableTarget] = useState<User | null>(null);
  const [mfaResetTarget, setMfaResetTarget] = useState<User | null>(null);
  const [actionTarget, setActionTarget] = useState<User | null>(null);
  const [resendingInviteId, setResendingInviteId] = useState<string | null>(null);

  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  const orgNameById = new Map((orgs.data ?? []).map((o) => [o.id, o.name]));

  // Reset paging when the page unmounts between visits
  useEffect(() => {
    return () => setOffset(0);
  }, []);

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteUser.mutateAsync(deleteTarget.id);
      toast.success(`Removed ${deleteTarget.full_name || deleteTarget.email}`);
      setDeleteTarget(null);
      setActionTarget(null);
    } catch (e) {
      toast.error('Remove failed', apiError(e));
    }
  };

  const handleDisable = async () => {
    if (!disableTarget) return;
    try {
      await disable.mutateAsync(disableTarget.id);
      toast.success('User disabled');
      setDisableTarget(null);
      setActionTarget(null);
    } catch (e) {
      toast.error('Failed', apiError(e));
    }
  };

  const handleMfaReset = async () => {
    if (!mfaResetTarget) return;
    try {
      await resetMfa.mutateAsync(mfaResetTarget.id);
      toast.success('MFA reset — user must re-enroll on next login');
      setMfaResetTarget(null);
      setActionTarget(null);
    } catch (e) {
      toast.error('Reset failed', apiError(e));
    }
  };

  const handleInvite = async () => {
    try {
      await invite.mutateAsync({
        email: email.trim(),
        role,
        organization_id: orgId || null,
        full_name: fullName.trim() || undefined,
      });
      toast.success('Invitation sent');
      setOpen(false);
      setEmail('');
      setFullName('');
    } catch (e) {
      toast.error('Invite failed', apiError(e));
    }
  };

  const handleResendInvite = async (inv: import('@/types/api').Invite) => {
    setResendingInviteId(inv.id);
    try {
      await resendInvite.mutateAsync({
        email: inv.email,
        role: inv.role as Role,
        organization_id: inv.organization_id,
      });
      toast.success(`Invite resent to ${inv.email}`);
    } catch (e) {
      toast.error('Resend failed', apiError(e));
    } finally {
      setResendingInviteId(null);
    }
  };

  // Filter to only pending (not yet accepted) invites
  const pendingInvites = (invites.data ?? []).filter((inv) => !inv.accepted_at);

  return (
    <>
      <Card
        title="Users"
        actions={
          <>
            <Button
              variant="secondary"
              onClick={() => setBulkOpen(true)}
              className="text-xs"
            >
              <Users size={14} /> Bulk invite
            </Button>
            <Button variant="secondary" onClick={() => setOpen(true)} className="text-xs">
              <UserPlus size={14} /> Invite user
            </Button>
          </>
        }
      >
        <p className="text-xs text-slate-500 mb-3">
          Invited users get an email with a link to set their password. Copy a manual reset link if email isn't configured.
        </p>
        {isLoading ? (
          <div className="flex justify-center py-10">
            <Spinner />
          </div>
        ) : isError ? (
          <div className="text-sm text-red-600">{apiError(error)}</div>
        ) : items.length === 0 ? (
          <EmptyState
            title="No users yet"
            description="Invite teammates or client users to get them into the portal."
            icon={<UserPlus size={24} />}
            action={
              <Button variant="secondary" onClick={() => setOpen(true)}>
                <UserPlus size={14} /> Invite user
              </Button>
            }
          />
        ) : (
          <>
          {/* Mobile card list */}
          <div className="sm:hidden divide-y divide-slate-100">
            {items.map((u) => (
              <button
                key={u.id}
                type="button"
                className="w-full text-left px-1 py-3 flex items-center gap-3 hover:bg-slate-50 active:bg-slate-100 transition-colors"
                onClick={() => setActionTarget(u)}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-sm text-slate-800 truncate">
                      {u.full_name || u.email}
                    </span>
                    {u.is_ooo && <Badge tone="yellow">OOO</Badge>}
                    {u.is_active ? (
                      <Badge tone="green">Active</Badge>
                    ) : (
                      <Badge tone="gray-strong">Disabled</Badge>
                    )}
                  </div>
                  <div className="text-xs text-slate-500 truncate mt-0.5">{u.email}</div>
                  <div className="text-xs text-slate-400 mt-0.5">{roleLabel(u.role)}</div>
                </div>
                <ChevronRight size={16} className="shrink-0 text-slate-400" />
              </button>
            ))}
          </div>

          {/* Desktop table */}
          <table className="hidden sm:table w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-slate-500 border-b border-slate-200">
                <th className="py-2 px-3 font-semibold">Name</th>
                <th className="py-2 px-3 font-semibold">Email</th>
                <th className="py-2 px-3 font-semibold">Organization</th>
                <th className="py-2 px-3 font-semibold">Role</th>
                <th className="py-2 px-3 font-semibold">Status</th>
                <th className="py-2 px-3 font-semibold">Last login</th>
                <th className="py-2 px-3 font-semibold text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((u) => (
                <tr key={u.id} className="border-b border-slate-100 hover:bg-slate-50">
                  <td className="py-2 px-3 text-slate-800">
                    <span className="inline-flex items-center gap-2">
                      {u.full_name}
                      {u.is_ooo && (
                        <span title={u.ooo_note ?? 'Currently out of office'}>
                          <Badge tone="yellow">OOO</Badge>
                        </span>
                      )}
                    </span>
                  </td>
                  <td className="py-2 px-3 text-slate-600">{u.email}</td>
                  <td className="py-2 px-3 text-slate-600">
                    {u.organization_id ? (
                      orgNameById.get(u.organization_id) ?? (
                        <span className="text-slate-400 italic">Unknown org</span>
                      )
                    ) : (
                      <span className="text-slate-400">—</span>
                    )}
                  </td>
                  <td className="py-2 px-3">
                    <Select
                      value={u.role}
                      onChange={(v) => {
                        const newRole = v as Role;
                        if (newRole === u.role) return;
                        updateUser.mutate(
                          { id: u.id, role: newRole },
                          {
                            onSuccess: () => toast.success(`Role changed to ${newRole}`),
                            onError: (err) => toast.error('Role change failed', apiError(err)),
                          },
                        );
                      }}
                      disabled={updateUser.isPending}
                      className="text-xs"
                      aria-label="Change role (sysadmin only)"
                      options={[
                        { value: 'sysadmin', label: 'System admin' },
                        { value: 'manager', label: 'Manager' },
                        { value: 'staff', label: 'Staff' },
                        { value: 'client_admin', label: 'Client admin' },
                        { value: 'client_user', label: 'Client user' },
                      ]}
                      placeholder=""
                    />
                  </td>
                  <td className="py-2 px-3">
                    {u.is_active ? (
                      <Badge tone="green">Active</Badge>
                    ) : (
                      <Badge tone="gray-strong">Disabled</Badge>
                    )}
                  </td>
                  <td className="py-2 px-3 text-xs text-slate-500">
                    {u.last_login_at ? format(new Date(u.last_login_at), 'PP p') : '—'}
                  </td>
                  <td className="py-2 px-3 text-right">
                    <div className="inline-flex items-center gap-1">
                      <Button
                        variant="ghost"
                        onClick={() => setDisableTarget(u)}
                        disabled={!u.is_active || disable.isPending}
                        className="text-xs"
                      >
                        Disable
                      </Button>
                      {u.mfa_enabled && (
                        <button
                          type="button"
                          title="Reset MFA"
                          onClick={() => setMfaResetTarget(u)}
                          className="p-1.5 text-slate-400 hover:text-amber-600 hover:bg-amber-50 rounded-[4px] transition-colors"
                        >
                          <ShieldOff size={14} />
                        </button>
                      )}
                      {me?.role === 'sysadmin' && u.id !== me.id && (
                        <button
                          type="button"
                          onClick={() => setDeleteTarget(u)}
                          title="Remove user"
                          className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-[4px] transition-colors"
                        >
                          <Trash2 size={14} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <Pagination
            total={total}
            limit={PAGE_LIMIT}
            offset={offset}
            onChange={setOffset}
            className="mt-2 border-t border-slate-100 pt-2"
          />
          </>
        )}
      </Card>

      {/* Pending invites — all roles */}
      {(pendingInvites.length > 0 || invites.isLoading) && (
        <Card title="Pending invites">
          {invites.isLoading ? (
            <div className="flex justify-center py-6">
              <Spinner />
            </div>
          ) : (
            <>
              {/* Mobile list */}
              <div className="sm:hidden divide-y divide-slate-100">
                {pendingInvites.map((inv) => (
                  <div key={inv.id} className="px-1 py-3 flex items-center gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-sm text-slate-800 truncate">{inv.email}</span>
                        <Badge tone="yellow">Pending</Badge>
                      </div>
                      <div className="text-xs text-slate-500 mt-0.5">{roleLabel(inv.role)} · expires {format(new Date(inv.expires_at), 'PP')}</div>
                      {inv.organization_id && (
                        <div className="text-xs text-slate-400 mt-0.5">{orgNameById.get(inv.organization_id) ?? inv.organization_id}</div>
                      )}
                    </div>
                    <div className="flex gap-2 shrink-0">
                      <Button
                        variant="secondary"
                        className="text-xs"
                        loading={resendingInviteId === inv.id}
                        disabled={resendingInviteId !== null || revokeInvite.isPending}
                        onClick={() => handleResendInvite(inv)}
                      >
                        Resend
                      </Button>
                      <Button
                        variant="ghost"
                        className="text-xs text-red-600 hover:text-red-700 hover:bg-red-50"
                        disabled={revokeInvite.isPending || resendingInviteId !== null}
                        onClick={() => {
                          if (confirm(`Revoke invite for ${inv.email}?`)) {
                            revokeInvite.mutate(inv.id, {
                              onSuccess: () => toast.success(`Invite for ${inv.email} revoked`),
                              onError: (e) => toast.error('Revoke failed', apiError(e)),
                            });
                          }
                        }}
                      >
                        Revoke
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
              {/* Desktop table */}
              <table className="hidden sm:table w-full text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wide text-slate-500 border-b border-slate-200">
                    <th className="py-2 px-3 font-semibold">Email</th>
                    <th className="py-2 px-3 font-semibold">Organization</th>
                    <th className="py-2 px-3 font-semibold">Role</th>
                    <th className="py-2 px-3 font-semibold">Status</th>
                    <th className="py-2 px-3 font-semibold">Expires</th>
                    <th className="py-2 px-3 font-semibold text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {pendingInvites.map((inv) => (
                    <tr key={inv.id} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="py-2 px-3 text-slate-800">{inv.email}</td>
                      <td className="py-2 px-3 text-slate-600">
                        {inv.organization_id ? (
                          orgNameById.get(inv.organization_id) ?? (
                            <span className="text-slate-400 italic">Unknown org</span>
                          )
                        ) : (
                          <span className="text-slate-400">—</span>
                        )}
                      </td>
                      <td className="py-2 px-3 text-slate-600">{roleLabel(inv.role)}</td>
                      <td className="py-2 px-3">
                        <Badge tone="yellow">Pending</Badge>
                      </td>
                      <td className="py-2 px-3 text-xs text-slate-500">
                        {format(new Date(inv.expires_at), 'PP')}
                      </td>
                      <td className="py-2 px-3 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            variant="secondary"
                            className="text-xs"
                            loading={resendingInviteId === inv.id}
                            disabled={resendingInviteId !== null || revokeInvite.isPending}
                            onClick={() => handleResendInvite(inv)}
                          >
                            Resend
                          </Button>
                          <Button
                            variant="ghost"
                            className="text-xs text-red-600 hover:text-red-700 hover:bg-red-50"
                            disabled={revokeInvite.isPending || resendingInviteId !== null}
                            onClick={() => {
                              if (confirm(`Revoke invite for ${inv.email}?`)) {
                                revokeInvite.mutate(inv.id, {
                                  onSuccess: () => toast.success(`Invite for ${inv.email} revoked`),
                                  onError: (e) => toast.error('Revoke failed', apiError(e)),
                                });
                              }
                            }}
                          >
                            Revoke
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </Card>
      )}

      {/* Mobile: user action sheet */}
      <Modal
        open={!!actionTarget}
        onClose={() => setActionTarget(null)}
        title={actionTarget?.full_name || actionTarget?.email || 'User'}
        size="sm"
      >
        {actionTarget && (
          <div className="space-y-1">
            <p className="text-xs text-slate-500 mb-3">{actionTarget.email} · {roleLabel(actionTarget.role)}</p>
            <div className="space-y-2">
              <div>
                <Label>Role</Label>
                <Select
                  value={actionTarget.role}
                  onChange={(v) => {
                    const newRole = v as Role;
                    if (newRole === actionTarget.role) return;
                    updateUser.mutate(
                      { id: actionTarget.id, role: newRole },
                      {
                        onSuccess: () => toast.success(`Role changed to ${newRole}`),
                        onError: (err) => toast.error('Role change failed', apiError(err)),
                      },
                    );
                  }}
                  disabled={updateUser.isPending}
                  options={staffOnly ? STAFF_ROLE_OPTIONS : ALL_ROLE_OPTIONS}
                  placeholder=""
                />
              </div>
              <div className="pt-2 border-t border-slate-100 flex flex-col gap-2">
                {actionTarget.is_active && (
                  <Button
                    variant="secondary"
                    onClick={() => { setDisableTarget(actionTarget); setActionTarget(null); }}
                    className="w-full justify-center"
                  >
                    Disable account
                  </Button>
                )}
                {actionTarget.mfa_enabled && (
                  <Button
                    variant="secondary"
                    onClick={() => { setMfaResetTarget(actionTarget); setActionTarget(null); }}
                    className="w-full justify-center"
                  >
                    <ShieldOff size={14} /> Reset MFA
                  </Button>
                )}
                {me?.role === 'sysadmin' && actionTarget.id !== me.id && (
                  <Button
                    variant="danger"
                    onClick={() => { setDeleteTarget(actionTarget); setActionTarget(null); }}
                    className="w-full justify-center"
                  >
                    <Trash2 size={14} /> Remove user
                  </Button>
                )}
              </div>
            </div>
          </div>
        )}
      </Modal>

      {/* Invite user modal */}
      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title="Invite user"
        footer={
          <>
            <Button variant="secondary" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleInvite}
              loading={invite.isPending}
              disabled={!email.trim() || ((role === 'client_admin' || role === 'client_user') && !orgId)}
              className="w-auto"
            >
              Send invite
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <p className="text-xs text-slate-500">
            Use this form to invite users who don't have a Microsoft 365 account. They'll receive an email with a link to set their password. Microsoft 365 users should sign in via <strong>Sign in with Microsoft</strong> instead.
          </p>
          <div>
            <Label htmlFor="inv-email">Email</Label>
            <Input id="inv-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
          </div>
          <div>
            <Label htmlFor="inv-name">Full name (optional)</Label>
            <Input id="inv-name" value={fullName} onChange={(e) => setFullName(e.target.value)} />
          </div>
          <div>
            <Label htmlFor="inv-role">Role</Label>
            <Select
              id="inv-role"
              value={role}
              onChange={(v) => { setRole(v as Role); setOrgId(''); }}
              options={staffOnly ? STAFF_ROLE_OPTIONS : ALL_ROLE_OPTIONS}
              placeholder=""
            />
          </div>
          {(role === 'client_admin' || role === 'client_user') && (
            <div>
              <Label htmlFor="inv-org">Organization</Label>
              <Select
                id="inv-org"
                value={orgId}
                onChange={setOrgId}
                options={(orgs.data ?? []).map((o) => ({ value: o.id, label: o.name }))}
                placeholder="-- Select organization --"
              />
              {!orgId && (
                <p className="text-xs text-amber-600 mt-1">Required for client roles.</p>
              )}
            </div>
          )}
        </div>
      </Modal>

      <BulkInviteModal open={bulkOpen} onClose={() => setBulkOpen(false)} />

      {/* Confirm disable */}
      <Modal
        open={!!disableTarget}
        onClose={() => setDisableTarget(null)}
        title="Disable account"
        size="sm"
        footer={
          <>
            <Button variant="secondary" onClick={() => setDisableTarget(null)}>Cancel</Button>
            <Button variant="danger" onClick={handleDisable} loading={disable.isPending} className="w-auto">
              Disable
            </Button>
          </>
        }
      >
        <p className="text-sm text-slate-700">
          Disable <span className="font-semibold">{disableTarget?.full_name || disableTarget?.email}</span>? They won't be able to sign in but their data is preserved.
        </p>
      </Modal>

      {/* Confirm MFA reset */}
      <Modal
        open={!!mfaResetTarget}
        onClose={() => setMfaResetTarget(null)}
        title="Reset MFA"
        size="sm"
        footer={
          <>
            <Button variant="secondary" onClick={() => setMfaResetTarget(null)}>Cancel</Button>
            <Button variant="danger" onClick={handleMfaReset} loading={resetMfa.isPending} className="w-auto">
              Reset MFA
            </Button>
          </>
        }
      >
        <p className="text-sm text-slate-700">
          Reset MFA for <span className="font-semibold">{mfaResetTarget?.full_name || mfaResetTarget?.email}</span>? They'll need to re-enroll on their next login.
        </p>
      </Modal>

      {/* Confirm remove */}
      <Modal
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        title="Remove user"
        footer={
          <>
            <Button variant="secondary" onClick={() => setDeleteTarget(null)}>
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={handleDelete}
              loading={deleteUser.isPending}
              className="w-auto"
            >
              Remove permanently
            </Button>
          </>
        }
      >
        <div className="space-y-2 text-sm text-slate-700">
          <p>
            Permanently remove{' '}
            <span className="font-semibold">
              {deleteTarget?.full_name || deleteTarget?.email}
            </span>{' '}
            from the system?
          </p>
          <p className="text-slate-500">
            Their uploaded files will remain. If they have files or records that
            prevent deletion, you'll be prompted to disable them instead.
          </p>
        </div>
      </Modal>
    </>
  );
}
